import {inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, take, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { APIRequestResources, ApiService, APIResponse, PaginationResponse } from '../../core';
import { handleError } from '../../core/util/util';
import { AddressFindDTO, CreateCustomer, CurrencyDTO, CustomerDTO } from '../../interfaces';

@Injectable({
    providedIn: 'root'
})
export class CustomerService {
    private readonly apiService = inject(ApiService);
    private readonly router = inject(Router);

    private readonly $all = new BehaviorSubject<CustomerDTO[]>([]);
    all = toSignal(this.$all, {initialValue: []});

    private readonly $active = new BehaviorSubject<CustomerDTO | undefined>(undefined);
    active = toSignal(this.$active, {initialValue: undefined});

    private readonly $activeSecondary = new BehaviorSubject<CustomerDTO | undefined>(undefined);
    activeSecondary = toSignal(this.$activeSecondary, {initialValue: undefined});

    constructor() {}

    getById = (id: any, refresh = true) => {
        return this.apiService.GET<CustomerDTO>(
            APIRequestResources.CustomerService,
            {
                endpoint: `${id}`
            }
        ).pipe(
            tap((res) => this.$active.next(res.data)),
            catchError(handleError)
        );
    }

    getByIdSecondary = (id: any, refresh = true) => {
        return this.apiService.GET<CustomerDTO>(
            APIRequestResources.CustomerService,
            {
                endpoint: `${id}`
            }
        ).pipe(
            tap((res) => this.$activeSecondary.next(res.data)),
            catchError(handleError)
        );
    }

    find = (params: any, refresh = true) => {
        return this.apiService.GET<PaginationResponse<CustomerDTO[]>>(
            APIRequestResources.CustomerService,
            {
                endpoint: `find`,
                params: params
            }
        ).pipe(
            tap(res => this.$all.next(res.data.data ?? [])),
            catchError(handleError)
        );
    }

    addressGet = (customerId: any, refresh = true) => {
        return this.apiService.GET<AddressFindDTO[]>(
            APIRequestResources.CustomerService,
            {
                endpoint: `address/${customerId}`
            }
        );
    }

    create = (customer: CreateCustomer) => {
        return this.apiService.POST<CurrencyDTO>(
            APIRequestResources.CustomerService,
            customer,
            {
                endpoint: ''
            }
        );
    }

    searchCustomers(params: any, refresh = true) {
        return this.apiService.GET<CustomerDTO[]>(
            APIRequestResources.CustomerService,
            {
                endpoint: `search`,
                params: params
            }
        ).pipe(
            tap(res => {
                // Ensure the data is an array and update the $all BehaviorSubject
                const customers = Array.isArray(res.data) ? res.data : [];
                console.log(customers);
                this.$all.next(customers);
            }),
            catchError(handleError)
        );
    }

    initial = () => {
        this.$active.next(undefined);
    }
}